import { Grapheme, Morpheme, Word } from '@/common/types/scopeAndSequence';
import { ControllerType } from '@/common/types/student';
import {
  playMorphemeAudio,
  playPartialCompletionAudio,
  playPhonemeAudio,
  playSentenceAudio,
  playWordAudio,
  playWordDefinition,
  soundOutGraphemeWithDelay,
  soundOutWord,
  stopAudio,
} from '@/common/utils/audio';
import { sanitize } from '@/common/utils/helpers';
import trackMixpanelEvent from '@/common/utils/mixpanel';
import {
  getArticulatoryGestures,
  getWordGraphemes,
} from '@/common/utils/scopeAndSequence';
import { EventBus } from '@/game/GameComponents/PhaserGame/EventBus';
import {
  GameInstance,
  getDailies,
  getGameMap,
  getGeneralGameState,
  getMorphMaster,
  getPhonemeForge,
  getSession,
  getStudent,
  getWordSums,
  getWordWeaver,
} from '@/game/GameComponents/PhaserGame/GameInstance';
import EventsManager from '@/game/GameComponents/PhaserGame/Managers/EventsManager';
import {
  CardFeedback,
  FullFeedbackOptions,
  KeyboardFeedback,
  MINIGAME_CARD_SOUND_OPTIONS,
} from '@/game/GameComponents/PhaserGame/Types/MinigameTypes';
import GAME_EVENTS from '@/game/GameComponents/PhaserGame/Utils/gameEventsDef';
import { PhaserAudioManager } from '@/game/GameComponents/PhaserGame/Utils/PhaserAudioManager';
import { waitForSeconds } from '@/game/GameComponents/PhaserGame/Utils/PhaserUtils';
import { RewardsManager } from '@/game/GameComponents/PhaserGame/Utils/RewardsManager';
import { createArticulatoryGesture } from '@/game/prefabs/StudentGame/ArticulatoryGesture';
import CardDropSite from '@/game/prefabs/StudentGame/CardDropSite';
import SingleCharacterText from '@/game/prefabs/StudentGame/SingleCharacterText';
import StudentGameCard from '@/game/prefabs/StudentGame/StudentGameCard';
import Minigame from '@/game/scenes/Minigame';

export class MinigameController {
  minigame: Minigame;

  constructor(minigame: Minigame) {
    this.minigame = minigame;
    EventsManager.registerEventOn(
      minigame,
      GAME_EVENTS.MINIGAME_CARD_DROPPED,
      this.handleMinigameCardDropped,
      this,
    );
  }

  startRound() {
    console.warn('startRound not implemented');
  }

  async playAudioForRound() {
    console.warn('playAudioForRound not implemented');
  }

  async finishActivity() {
    const { currentActivity } = GameInstance.instance.session;
    if (!currentActivity) {
      throw new Error('No activity found');
    }

    // Track game finished event
    const gameName = getGeneralGameState().currentGame;
    const student = getStudent();
    const studentId = student.id;

    if (studentId && gameName) {
      trackMixpanelEvent(`${gameName} Finished`);
    }

    GameInstance.instance.endActivity();
  }

  playerCompleteActivity(activityName: string) {
    const organisationId = getStudent().organisationId;
    let newDailyCompleted = false;

    switch (activityName) {
      case 'winDuel':
        const duelState = getDailies().duel;
        if (duelState.shouldCompleteDuel && !duelState.hasCompletedDuel) {
          // We just completed our duel daily.
          newDailyCompleted = true;
          trackMixpanelEvent('User Completed Duel Daily', {});
        }
        GameInstance.instance.updateDailyActivities({
          duel: {
            ...getDailies().duel,
            hasCompletedDuel: true,
          },
        });
        break;
      case 'mysticMatch':
      case 'wordWeaver':
      case 'phonemeForge':
      case 'trickyWords':
      case 'sentenceBuilder':
      case 'wordRifts':
      case 'wordSums':
      case 'morphmaster':
        const minigamesState = getDailies().minigames;
        if (
          minigamesState.numCompletedMinigames <
          minigamesState.targetNumMinigames
        ) {
          // We just completed a minigame daily.
          newDailyCompleted = true;
          trackMixpanelEvent('User Completed Daily Minigame', {
            activityName,
            organisationId,
          });
        }

        // Mixpanel mastery tracking.
        const accuracy = getSession().currentActivity?.accuracy || 0;
        if (accuracy >= 90) {
          trackMixpanelEvent('User Mastered Minigame', {
            game: activityName,
            accuracy,
            organisationId,
          });
        }

        GameInstance.instance.updateDailyActivities({
          minigames: {
            ...getDailies().minigames,
            numCompletedMinigames: Math.min(
              getDailies().minigames.targetNumMinigames,
              getDailies().minigames.numCompletedMinigames + 1,
            ),
          },
        });
    }

    if (
      newDailyCompleted &&
      RewardsManager.getTotalDailyProgress(getDailies()) === 1
    ) {
      trackMixpanelEvent('User Completed All Daily Activities', {
        organisationId,
      });
    }

    const dailyActivities = getDailies();
    GameInstance.instance.updateStudent({ dailyActivities });
    EventBus.emit(GAME_EVENTS.MINIGAME_ENDED);
  }

  completedNode() {
    const gameName = getGeneralGameState().currentGame;
    const gameMap = getGameMap();
    const student = getStudent();
    const studentId = student.id;
    const currentNodeIndex = gameMap.currentNodeIndex;
    if (currentNodeIndex === -1) {
      console.error('No current node index found');
      return;
    }

    if (!studentId) {
      console.error('No student ID found');
      return;
    }

    GameInstance.instance.recordGameCompleted(gameName);
    GameInstance.instance.updateGameMap({
      isVisitingNode: false,
      currentNodeIndex: -1,
    });
    GameInstance.instance.updateGeneralGameState({
      lastCompletedGame: gameName,
    });
    EventBus.emit(GAME_EVENTS.NODE_COMPLETED);
  }

  handleQuit(completedSuccessfully: boolean = false) {
    const currentGame = getGeneralGameState().currentGame;
    const currentSession = getSession().currentSession;
    trackMixpanelEvent(`Learner quit ${currentGame}`, {
      activityName: currentGame,
      sessionId: currentSession?.id,
    });
    if (!completedSuccessfully) {
      trackMixpanelEvent('Game exited early', {
        activityName: currentGame,
        sessionId: currentSession?.id,
      });
    }
    stopAudio();
    this.minigame.time.clearPendingEvents();
    GameInstance.instance.updateGeneralGameState({
      progress: 0,
    });

    switch (getGeneralGameState().currentGame) {
      case 'mysticMatch':
        GameInstance.instance.resetMysticMatch();
        break;
      case 'wordWeaver':
        GameInstance.instance.resetWordWeaver();
        break;
      case 'phonemeForge':
        GameInstance.instance.resetPhonemeForge();
        break;
      case 'sentenceBuilder':
        GameInstance.instance.resetSentenceBuilder();
        break;
      case 'wordRifts':
        GameInstance.instance.resetWordRifts();
        break;
      case 'wordSums':
        GameInstance.instance.resetWordSums();
        break;
      case 'heartWords':
        GameInstance.instance.resetHeartWords();
        break;
    }

    GameInstance.instance.session.currentActivity = null;
    GameInstance.instance.resetAttempts();
  }

  autoWinMinigame() {
    const studentId = getStudent().id;
    if (!studentId) {
      console.error('No student ID found');
      return;
    }

    GameInstance.instance.updateGeneralGameState({
      progress: 100,
    });
  }

  canSubmitAnswer(): boolean {
    const control = this.minigame.getControlScheme(
      getStudent().settings.controllerType,
    );

    switch (control) {
      case ControllerType.KEYBOARD:
        // At least one letter
        return this.minigame.getTypedInput().length > 0;

      case ControllerType.CLICK:
      case ControllerType.DRAG:
        // Something played for all slots
        return this.minigame.getBoardCards().length === this.getMaxBoardCards();
    }
  }

  createCardList(_minigameScene: Minigame): {
    dealtCards: (StudentGameCard | null)[];
    boardCards: (StudentGameCard | null)[];
  } {
    return { dealtCards: [], boardCards: [] };
  }

  getMaxBoardCards(): number {
    return 1;
  }

  getShouldDisplayBoardSlots(): boolean {
    return false;
  }

  // Does full feedback for cards or letters, then riffles them at the end if enabled.
  async fullFeedback(
    feedback: CardFeedback[] | KeyboardFeedback[],
    allCorrect: boolean = true,
    options?: FullFeedbackOptions,
  ) {
    const doArticulatoryGestures = this.shouldShowArticulatoryGestures();
    const doWordSegmentation = this.shouldShowWordSegmentation();

    // Case for just skipping all of the individual item feedback and going straight to the riffle.
    const shouldSkipIndividualItemFeedback =
      !options?.forceIndividualFeedback &&
      allCorrect &&
      !options?.disableRiffle &&
      !doArticulatoryGestures &&
      !doWordSegmentation;

    if (!shouldSkipIndividualItemFeedback) {
      for (var i = 0; i < feedback.length; i++) {
        await this.doItemFeedback(feedback[i], true, options?.forceReadout);
      }
      await waitForSeconds(this.minigame.time, 0.4);
    }

    if (doWordSegmentation && allCorrect) {
      // Override this in subclasses if needed.
      await this.readFinalAnswer();
    }

    if (allCorrect && !options?.disableRiffle) {
      PhaserAudioManager.playSFX(
        this.minigame,
        'mastered-sfx',
        MINIGAME_CARD_SOUND_OPTIONS,
      );
      await this.minigame.riffleItems();
    }
    // Generic delay.
    await waitForSeconds(this.minigame.time, 0.5);

    await options?.feedbackCompleteCallback?.(feedback);
  }

  async doItemFeedback(
    feedback: CardFeedback | KeyboardFeedback,
    playSFX: boolean = true,
    forceReadout: boolean = false,
  ) {
    const isCardFeedback = 'card' in feedback;
    const isLettersFeedback = 'letters' in feedback;

    if (isCardFeedback) {
      await this.doCardFeedback(
        feedback as CardFeedback,
        playSFX,
        forceReadout,
      );
    } else if (isLettersFeedback) {
      await this.doLetterFeedback(
        feedback as KeyboardFeedback,
        playSFX,
        forceReadout,
      );
    } else {
      console.error('Invalid feedback type');
    }
  }

  // Does full feedback for a card, including highlighting it neutrally if
  // articulatory gestures or word segmentation are enabled.
  async doCardFeedback(
    feedback: CardFeedback,
    playSFX: boolean = true,
    forceReadout: boolean = false,
  ) {
    const { card, isCorrect, grapheme } = feedback;

    const doArticulatoryGestures = this.shouldShowArticulatoryGestures();
    const doWordSegmentation = this.shouldShowWordSegmentation();

    if (doArticulatoryGestures || doWordSegmentation || forceReadout) {
      await this.highlightItem(
        card,
        'neutral',
        playSFX,
        forceReadout,
        grapheme,
      );
    }
    if (isCorrect) {
      await this.highlightItem(card, 'correct', playSFX, undefined, grapheme);
    } else {
      await this.highlightItem(card, 'incorrect', playSFX, undefined, grapheme);
    }
    // Manual delay for clarity.
    await waitForSeconds(this.minigame.time, 0.1);
  }

  async doLetterFeedback(
    feedback: KeyboardFeedback,
    playSFX: boolean = true,
    forceReadout: boolean = false,
  ) {
    const doArticulatoryGestures = this.shouldShowArticulatoryGestures();
    const doWordSegmentation = this.shouldShowWordSegmentation();

    if (doArticulatoryGestures || doWordSegmentation || forceReadout) {
      await this.highlightItem(
        feedback.letters,
        'neutral',
        playSFX,
        forceReadout,
        feedback.grapheme,
        feedback.morpheme,
        feedback.word,
      );
    }
    if (feedback.isCorrect) {
      await this.highlightItem(
        feedback.letters,
        'correct',
        playSFX,
        forceReadout,
        feedback.grapheme,
        feedback.morpheme,
        feedback.word,
      );
    } else {
      await this.highlightItem(
        feedback.letters,
        'incorrect',
        playSFX,
        forceReadout,
        feedback.grapheme,
        feedback.morpheme,
        feedback.word,
      );
    }
    // Manual delay for clarity.
    await waitForSeconds(this.minigame.time, 0.1);
  }

  // Highlights a card or letter sequence with the appropriate feedback type.
  // Also plays the appropriate SFX if enabled.
  async highlightItem(
    item: StudentGameCard | SingleCharacterText[],
    feedbackType: 'correct' | 'incorrect' | 'neutral',
    playSFX: boolean = true,
    forceReadout: boolean = false,
    grapheme?: Grapheme,
    morpheme?: Morpheme,
    word?: Word,
  ) {
    const isCard = item instanceof StudentGameCard;
    const isLetters = !isCard;
    const card = item as StudentGameCard;
    const doArticulatoryGestures = this.shouldShowArticulatoryGestures();
    const doWordSegmentation = this.shouldShowWordSegmentation();

    const agnosticHighlight = async () => {
      if (isCard) {
        switch (feedbackType) {
          case 'correct':
            await item.showCorrectFeedback();
            break;
          case 'incorrect':
            await item.showIncorrectFeedback();
            break;
          case 'neutral':
            await item.showNeutralFeedback();
            break;
        }
      } else {
        item.forEach((letter) => {
          switch (feedbackType) {
            case 'correct':
              letter.doSuccessFeedback();
              break;
            case 'incorrect':
              letter.doIncorrectFeedback();
              break;
            case 'neutral':
              letter.doNeutralFeedback();
              break;
          }
        });
        await waitForSeconds(this.minigame.time, 0.25);
      }
    };

    if (feedbackType === 'correct') {
      if (playSFX) {
        PhaserAudioManager.playSFX(
          this.minigame,
          'correct-sfx',
          MINIGAME_CARD_SOUND_OPTIONS,
        );
      }
      await agnosticHighlight();
    } else if (feedbackType === 'incorrect') {
      if (playSFX) {
        PhaserAudioManager.playSFX(
          this.minigame,
          'incorrect-sfx',
          MINIGAME_CARD_SOUND_OPTIONS,
        );
      }
      await agnosticHighlight();
    } else if (feedbackType === 'neutral') {
      const wordDelay = getStudent().settings.audio.audioDelay;
      const thisGrapheme = grapheme ?? card?.cardData?.grapheme;
      const thisWord = word ?? card?.cardData?.word;
      const thisMorpheme = morpheme ?? card?.cardData?.morpheme;
      // Show an articulatory gesture if enabled.
      if (doArticulatoryGestures) {
        if (thisGrapheme?.pronunciation) {
          // Wait for the audio to play before continuing.
          agnosticHighlight();
          if (isCard) {
            card.showArticulatoryGesture(thisGrapheme?.pronunciation);
            await soundOutGraphemeWithDelay(
              thisGrapheme.pronunciation,
              wordDelay,
            );
          } else if (isLetters) {
            // Show articulatory gesture for letters.
            const wordDelay = getStudent().settings.audio.audioDelay;
            const worldX = item[0].getWorldTransformMatrix().tx;
            const worldY = item[0].getWorldTransformMatrix().ty;
            createArticulatoryGesture(
              this.minigame,
              worldX,
              worldY - 100,
              thisGrapheme.pronunciation,
            );
            await soundOutGraphemeWithDelay(
              thisGrapheme.pronunciation,
              wordDelay,
            );
          }
        }
      } else if (doWordSegmentation || forceReadout) {
        if (thisGrapheme?.pronunciation) {
          agnosticHighlight();
          await soundOutGraphemeWithDelay(
            thisGrapheme.pronunciation,
            wordDelay,
          );
        }
        if (thisWord?.spelling) {
          agnosticHighlight();
          await playWordAudio(sanitize(thisWord.spelling));
        }
        if (thisMorpheme?.spelling) {
          agnosticHighlight();
          await playMorphemeAudio(sanitize(thisMorpheme.spelling));
        }
        if (card.cardData?.spelling) {
          agnosticHighlight();
          await playWordAudio(sanitize(card.cardData?.spelling));
        }
      } else {
        // Just wait for the feedback to play.
        await agnosticHighlight();
      }
    }
  }

  async highlightLetters(
    _letters: SingleCharacterText[],
    _isCorrect: boolean,
    _playSFX: boolean = true,
  ) {}

  async showFeedback(
    feedback: CardFeedback[] | KeyboardFeedback[],
    options: FullFeedbackOptions,
  ) {
    const { correctAnswer, partiallyCorrectAnswer } = options;
    this.minigame.setBoardInteractive(false);
    await this.fullFeedback(feedback, correctAnswer, options);
    if (!correctAnswer) {
      if (partiallyCorrectAnswer) {
        // Case for partially correct answer.
        await this.handlePartiallyCorrectFeedbackComplete(feedback);
      }

      await this.handleIncorrectFeedbackComplete(feedback);
      // Let the user try again.
      await waitForSeconds(this.minigame.time, 0.05);
      this.minigame.setBoardInteractive(true);
      this.minigame.setSubmissionPossible(this.canSubmitAnswer());
    }
  }

  async checkMinigameAnswer() {
    console.warn('checkMinigameAnswer not implemented');
  }

  async playInstructions() {
    console.warn('playInstructions not implemented');
  }

  getItemFeedback(): CardFeedback[] | KeyboardFeedback[] {
    const student = getStudent();
    if (!student) {
      console.error('No student found');
      return [];
    }
    if (
      this.minigame.getControlScheme(student.settings.controllerType) ===
      'keyboard'
    ) {
      return this.getKeyboardFeedback();
    }
    return this.getCardFeedback();
  }

  getCardFeedback(): CardFeedback[] {
    return [];
  }

  getKeyboardFeedback(): KeyboardFeedback[] {
    return [];
  }

  async playCurrentWordDefinition() {
    const word = this.getCurrentWord();
    if (!word) {
      console.error('No word found');
      return;
    }
    stopAudio();
    const student = getStudent();
    const studentId = student.id;
    if (studentId) {
      trackMixpanelEvent('Definition button clicked', {
        word: word,
      });
    }

    try {
      this.minigame.showDefinitionForWord(word);
    } catch (error) {
      console.error('Error showing helper icon', error);
    }

    await playWordDefinition(word);
  }

  getCurrentWord(): Word | null {
    const currentGame = getGeneralGameState().currentGame;
    switch (currentGame) {
      case 'phonemeForge':
        return getPhonemeForge().wordToCreate;
      case 'wordSums':
        return getWordSums().wordToBuild;
      case 'wordWeaver':
        return getWordWeaver().wordToBuild;
      case 'morphMaster':
        return getMorphMaster().wordToCreate;
    }
    return null;
  }

  async playWordInSentence() {
    const word = this.getCurrentWord();
    if (!word) {
      console.error('No word found');
      return;
    }
    stopAudio();
    const student = getStudent();
    const studentId = student.id;
    if (studentId) {
      trackMixpanelEvent('Example button clicked', {
        word: word.spelling,
      });
    }

    try {
      this.minigame.showExampleForWord(word);
    } catch (error) {
      console.error('Error showing icon', error);
    }

    await playWordAudio(word.spelling);
    await playSentenceAudio(word.example);
    await playWordAudio(word.spelling);
  }

  async playGraphemeExampleWord() {
    console.warn('playGraphemeExampleWord not implemented');
  }

  async readFinalAnswer() {}

  shouldFillBoardWithSlots(): boolean {
    return false;
  }

  async soundOutWord(word: Word) {
    const doWordSegmentation =
      getStudent().settings.wordSegmentationEnabled &&
      this.canShowWordSegmentation();
    const audioDelay = getStudent().settings.audio.audioDelay;
    if (doWordSegmentation) {
      const wordGraphemes = getWordGraphemes(word);
      await soundOutWord(
        wordGraphemes,
        audioDelay,
        wordGraphemes,
        this.handleSegmentationAudioPlayed.bind(this),
      );
    }
    await playWordAudio(word.spelling.toLowerCase());
  }

  handleMinigameCardDropped(data: {
    card: StudentGameCard;
    dropSiteName: string;
    targetIndex: number;
  }) {
    if (this.shouldPlayPhonemeOnCardDrop()) {
      const phoneme = data.card?.cardData?.grapheme?.pronunciation;
      if (phoneme) {
        playPhonemeAudio(phoneme);
        if (this.shouldShowArticulatoryGestures()) {
          data.card.showArticulatoryGesture(phoneme);
        }
      }
    }
  }

  async handleIncorrectFeedbackComplete(
    _feedback: CardFeedback[] | KeyboardFeedback[],
  ) {
    // Potentially bring this back as an option later.
    // if ('card' in feedback[0]) {
    //   this.returnIncorrectCardsToHand(feedback as CardFeedback[]);
    // }
    await waitForSeconds(this.minigame.time, 0.25);
    await this.playInstructions();
  }

  async handlePartiallyCorrectFeedbackComplete(
    _feedback: CardFeedback[] | KeyboardFeedback[],
  ) {
    // For now, only show this on typing games.
    const student = getStudent();
    if (!student) {
      console.error('No student found');
      return;
    }
    if (
      this.minigame.getControlScheme(student.settings.controllerType) !==
      'keyboard'
    ) {
      return;
    }
    // Add '-' to the end of the answer.
    const playerTypingInput = this.minigame.getPlayerTypingInputs()[0];

    const newCharacter = playerTypingInput.addCharacter(
      '-',
      playerTypingInput.getCharacterArray().length,
    );
    newCharacter.doIncorrectFeedback();
    PhaserAudioManager.playSFX(this.minigame, 'incorrect-sfx');
    await waitForSeconds(this.minigame.time, 0.75);
    playerTypingInput.removeCharacter(
      playerTypingInput.getCharacterArray().length - 1,
    );
    await waitForSeconds(this.minigame.time, 0.75);
    await playPartialCompletionAudio();
  }

  // Handles showing articulatory gestures when a grapheme is played
  // via word segmentation for a word that's not on the cards.
  handleSegmentationAudioPlayed(
    grapheme: Grapheme,
    _index: number,
    audioDelay: number,
  ) {
    if (!this.shouldShowArticulatoryGestures()) return;

    const gestures = getArticulatoryGestures(grapheme.pronunciation);
    this.minigame.showHelperArticulatoryGestures(gestures, audioDelay);
  }

  initializeNewMinigameCard(
    newCard: StudentGameCard,
    dropSite: CardDropSite,
    index?: number,
  ) {
    if (index === undefined) {
      dropSite.pushCard(newCard);
    } else {
      dropSite.addCard(newCard, index);
    }
  }

  canShowArticulatoryGestures(): boolean {
    // Override this in subclasses if needed.
    return true;
  }

  shouldShowArticulatoryGestures(): boolean {
    const student = getStudent();
    if (!student) {
      console.error('No student found');
      return false;
    }
    return (
      this.canShowArticulatoryGestures() &&
      student.settings.articulatoryGesturesEnabled
    );
  }

  canShowWordSegmentation(): boolean {
    // Override this in subclasses if needed.
    return true;
  }

  shouldShowWordSegmentation(): boolean {
    const student = getStudent();
    if (!student) {
      console.error('No student found');
      return false;
    }
    return (
      this.canShowWordSegmentation() && student.settings.wordSegmentationEnabled
    );
  }

  canPlayPhonemeOnCardDrop() {
    // Override this in subclasses if needed.
    return true;
  }

  shouldPlayPhonemeOnCardDrop(): boolean {
    const student = getStudent();
    if (!student) {
      console.error('No student found');
      return false;
    }
    return (
      this.canPlayPhonemeOnCardDrop() && student.settings.playPhonemeOnCardDrop
    );
  }

  returnIncorrectCardsToHand(feedback: CardFeedback[]) {
    if (!(feedback as CardFeedback[])) return;
    const incorrectCards = feedback
      .filter((feedback) => !feedback.isCorrect)
      .map((feedback) => feedback.card);
    incorrectCards.forEach((card) => {
      this.minigame
        .getBoardDropSite()
        .transferCard(card, this.minigame.getHandDropSite(), 0);
    });
  }

  updateRules(_scheme: ControllerType) {}
}
